import get from 'lodash.get';
import axios from 'axios';
import store from '@/store';
import router from '@/router';
import { isLocked } from '@/utilities/JwtRefreshTokensLock';
import { getDifferenceInMinutes } from '@/utilities/DateUtils';
import { decodeJwt } from '@/utilities';
import dayjs from 'dayjs';
import dayjsUtcPlugin from 'dayjs/plugin/utc';
import userMessages from '../constants/userMessages';

dayjs.extend(dayjsUtcPlugin);

function setClockOffsetFromServer(response) {
  const acvServerTime = parseInt(
    get(response, 'headers.acv-server-time', 0),
    10,
  );
  if (acvServerTime) {
    store.dispatch('auctions/updateClockOffset', acvServerTime);
  }
}

/**
 *
 * @param {*} configUrl Config url from response
 */
function usingJWTAuth(configUrl) {
  // Apply token refresh workflow to these urls
  const gatewayWhitelist = [
    import.meta.env.VITE_HYDRA_API_BASE_URI,
    import.meta.env.VITE_EASY_PASS_BASE_URI,
    import.meta.env.VITE_CAPITAL_GATEWAY_BASE_URI,
    import.meta.env.VITE_TITLES_SERVICE_BASE_URI,
    import.meta.env.VITE_MAX_DIGITAL_BASE_URI,
    import.meta.env.VITE_MY_ACV_BASE_URI,
    import.meta.env.VITE_FEATURE_FLAG_URI,
    import.meta.env.VITE_ENT_MICRO_BASE_URI,
    import.meta.env.VITE_ACV_AUDIO_BASE_URI_V2,
    import.meta.env.VITE_SEARCH_SERVICE_API_URL,
    import.meta.env.VITE_ACV_PAYMENT_BASE_URI,
    import.meta.env.VITE_PAYMENT_SERVICE_BASE_URI,
    import.meta.env.VITE_ORGANIZATION_SERVICE_BASE_URI,
    import.meta.env.VITE_TYK_TO_EASY_PASS_BFF_URL,
    import.meta.env.VITE_SALES_FORCE_FEATURE_SUBSCRIPTION_URL,
    import.meta.env.VITE_APES_GATEWAY_URI,
    import.meta.env.VITE_NOTIFICATION_SERVICE_URI,
    import.meta.env.VITE_ACV_EULA_URL,
    import.meta.env.VITE_ACV_INVENTORY_ONBOARDING_BASE_URI,
    import.meta.env.VITE_ACV_GUARANTEE_ENGINE_BASE_URI,
    import.meta.env.VITE_ACV_GUARANTEE_SALE_ENGINE_BASE_URI,
    import.meta.env.VITE_LEAD_SENDING_SERVICE_URI,
    import.meta.env.VITE_INVENTORY_GATEWAY_BASE_URI,
    import.meta.env.VITE_EULA_TOS_SERVICE_URI,
    import.meta.env.VITE_TEST_TRACK_BASE_URI,
    import.meta.env.VITE_TRANSPORT_MARKETPLACE_GATEWAY_BASE_URI,
    import.meta.env.VITE_AUCTION_LAUNCH_BASE_URI,
    import.meta.env.VITE_REWARDS_URI,
    import.meta.env.VITE_TYK_LISTING_SERVICE_BASE_URI,
  ];
  return gatewayWhitelist.some((url) => configUrl.includes(url));
}

function isTokenEndpoint(configUrl) {
  const urlExcludeList = [
    '/tokenrefresh',
    '/login',
    '/legacylogin/v2',
    '/code',
    '/sso',
    '/token',
  ];
  return (!urlExcludeList.every((url) => (configUrl.includes('/login_data') ? true : !configUrl.includes(url))));
}

/**
 *
 * @param {*} configUrl Config url from response
 */
function shouldRefreshToken(configUrl) {
  // Exclude these urls from a token refresh workflow
  return usingJWTAuth(configUrl) && !isTokenEndpoint(configUrl);
}

// Determines which endpoints should allow cancel tokens to trigger on idle
function allowCancelTokenForUrl(configUrl) {
  return !isTokenEndpoint(configUrl) && !configUrl.includes('feature_flag_group');
}

function shouldPushToLogout(configUrl) {
  return (
    !configUrl.includes('/login')
    && store.state.route.name !== 'login'
    && store.state.route.name !== 'logout'
    && !isTokenEndpoint(configUrl)
  );
}

const interceptors = {
  request: {
    async preflight(config) {
      let updatedConfig = {
        ...config,
      };

      const { url: configUrl = '' } = updatedConfig;

      /* eslint-disable-next-line no-unsafe-optional-chaining */
      const { isPubnubOnline, isIdle } = store?.getters;

      // cancel request for allowable endpoints as long as pubnub remains offline or client is idle
      if (allowCancelTokenForUrl(configUrl) && (!isPubnubOnline || isIdle)) {
        if (import.meta.env.DEV && store?.state?.devTools?.analyticsLoggingEnabled) {
          window.console.log('This request was cancelled: ', configUrl, 'isPubnubOnline', isPubnubOnline, 'isIdle', isIdle);
        }

        const { CancelToken } = axios;
        updatedConfig.cancelToken = new CancelToken((cancel) => cancel(
          userMessages.errors.canceledFetch,
        ));
      }

      const {
        lastUpdated,
        jwtToken,
        refreshToken,
        lastRefreshFailed = false,
      } = await store.dispatch('getJwtTokens');

      const decodedJwtToken = decodeJwt(jwtToken) ?? '';
      const jwtExpirationVal = new Date(0);
      jwtExpirationVal.setUTCSeconds(decodedJwtToken?.exp ?? 0);

      const canRefreshTokens = !!(jwtToken && refreshToken && lastUpdated);

      if (shouldRefreshToken(configUrl)
        && canRefreshTokens
        && !lastRefreshFailed
        && !configUrl.includes('acv-api')
      ) {
        const refreshIsLocked = isLocked();
        const currentDate = new Date();

        const jwtDifferenceInMinutes = getDifferenceInMinutes(
          jwtExpirationVal, // start date
          currentDate, // end date
        );

        const luDifferenceInMinutes = getDifferenceInMinutes(
          currentDate,
          lastUpdated,
        );

        const atLeast12MinsSinceLastUpdate = luDifferenceInMinutes >= 12;

        const tokensCloseToExpiration = jwtDifferenceInMinutes < 3
          || atLeast12MinsSinceLastUpdate;

        if (window.acvNative) {
          if (tokensCloseToExpiration) {
            let credentials = await window.acvNative.refreshJwt();

            credentials = JSON.parse(credentials);
            await store.dispatch('setJwtTokens', {
              jwtToken: credentials.jwtToken,
              refreshToken: credentials.refreshToken,
            });
          }
        } else if (!refreshIsLocked && tokensCloseToExpiration) {
          await store.dispatch('refreshJwtTokens');
        } else if (refreshIsLocked && tokensCloseToExpiration) {
          await store.dispatch('waitForOtherWindowToRefreshJwtToken');
        }
        const { jwtToken: afterRefreshJwtToken } = await store.dispatch(
          'getJwtTokens',
        );
        updatedConfig = {
          ...updatedConfig,
          headers: {
            ...updatedConfig.headers,
            Authorization: `Bearer ${afterRefreshJwtToken}`,
          },
        };

        const { lastCheckedTosDate } = store?.getters ?? null;
        const currentTimestamp = dayjs(new Date());
        let differenceInDays = 0;
        if (lastCheckedTosDate !== null) {
          differenceInDays = currentTimestamp.diff(lastCheckedTosDate, 'day');
        }
        const currentTosCheckInProgress = store?.getters?.currentTosCheckInProgress;
        if (!currentTosCheckInProgress
          && (lastCheckedTosDate === null || differenceInDays >= 1)) {
          await store.dispatch('checkIfNeedTosAcceptance');
        }
      }
      return updatedConfig;
    },
    error(error) {
      return Promise.reject(error);
    },
  },
  response: {
    success(response) {
      setClockOffsetFromServer(response);
      return response;
    },
    async error(error) {
      setClockOffsetFromServer(get(error, 'response'));
      const errorType = get(error, 'response.status') || 400;
      const { config = {} } = error;
      const { url: configUrl = '' } = config;
      if (errorType === 400 && configUrl.includes('api/v2/tokenrefresh')) {
        if (store.state.tours.currentTourPath) {
          store.dispatch('tours/setCurrentTourPath', null);
        }
        router.push('/logout');
      } else if (errorType === 401) {
        if (window.acvNative) {
          let credentials = await window.acvNative.refreshJwt();

          credentials = JSON.parse(credentials);
          await store.dispatch('setJwtTokens', {
            jwtToken: credentials.jwtToken,
            refreshToken: credentials.refreshToken,
          });
          window.acvNative.reloadWebView();
        } else if (shouldPushToLogout(configUrl)) {
          if (store.state.tours.currentTourPath) {
            store.dispatch('tours/setCurrentTourPath', null);
          }
          router.push('/logout');
        }
      }
      return Promise.reject(error);
    },
  },
};

function installInterceptors(axiosInstance) {
  axiosInstance
    .interceptors
    .request
    .use(
      interceptors.request.preflight,
      interceptors.request.error,
    );
  axiosInstance
    .interceptors
    .response
    .use(
      interceptors.response.success,
      interceptors.response.error,
    );

  return axiosInstance;
}

export { interceptors, installInterceptors };
